import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Loader2, Mail, Lock, Building2, BarChart3, PieChart, Activity } from 'lucide-react';
import { toast } from 'sonner';
import WorldGlobe from '../components/ui/WorldGlobe';
import usePublicSignup, { PRIVATE_PILOT_MESSAGE } from '../components/auth/usePublicSignup';

const LOGO_URL = '/ciaolead-logo-full.png';

const Signup = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [privacyConsent, setPrivacyConsent] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { signup } = useAuth();
  const navigate = useNavigate();
  // PILOT-ACCESS-002: closed signup shows the private-pilot notice instead of the form. The
  // server refuses the request regardless; this only spares the visitor a form that fails.
  const publicSignup = usePublicSignup();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password || !companyName) {
      toast.error('Please fill in all fields');
      return;
    }

    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    // P6.7: privacy consent is mandatory for new signups.
    if (!privacyConsent) {
      toast.error('Please accept the Privacy Policy to continue');
      return;
    }

    setIsLoading(true);
    try {
      await signup(email, password, companyName, privacyConsent);
      toast.success('Account created successfully!');
      navigate('/dashboard');
    } catch (error) {
      const message = error.response?.data?.detail || 'Signup failed. Please try again.';
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-[var(--bg-page)] world-stars" data-testid="signup-page">
      {/* Left Panel - Form */}
      <div className="relative z-10 w-full lg:w-[42%] flex items-center justify-center p-8">
        <div className="w-full max-w-md space-y-8 world-card p-8 lg:p-10">
          {/* Logo */}
          <div className="flex justify-center lg:justify-start">
            <img
              src={LOGO_URL}
              alt="CiaoLead"
              className="max-w-[200px] w-full h-auto object-contain"
              data-testid="signup-logo"
            />
          </div>

          <Card className="border-0 shadow-none bg-transparent">
            <CardHeader className="px-0">
              <CardTitle className="text-3xl font-bold text-white">
                Create your account
              </CardTitle>
              <CardDescription className="text-slate-400 text-base">
                Start qualifying leads smarter today
              </CardDescription>
            </CardHeader>
            <CardContent className="px-0">
              {publicSignup === false ? (
                <div className="space-y-3" data-testid="signup-private-pilot">
                  <p className="text-base font-medium text-white">{PRIVATE_PILOT_MESSAGE}</p>
                  <p className="text-sm text-slate-400">
                    Accounts are created from a personal invitation. If you received one, use the
                    link in that message.
                  </p>
                </div>
              ) : (
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="companyName" className="text-slate-200 font-medium">
                    Company name
                  </Label>
                  <div className="relative">
                    <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 z-10" />
                    <Input
                      id="companyName"
                      type="text"
                      placeholder="Your company name"
                      value={companyName}
                      onChange={(e) => setCompanyName(e.target.value)}
                      className="pl-10 h-11 world-input"
                      data-testid="signup-company-input"
                      disabled={isLoading}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email" className="text-slate-200 font-medium">
                    Work email
                  </Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 z-10" />
                    <Input
                      id="email"
                      type="email"
                      placeholder="you@company.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="pl-10 h-11 world-input"
                      data-testid="signup-email-input"
                      disabled={isLoading}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password" className="text-slate-200 font-medium">
                    Password
                  </Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 z-10" />
                    <Input
                      id="password"
                      type="password"
                      placeholder="Create a strong password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="pl-10 h-11 world-input"
                      data-testid="signup-password-input"
                      disabled={isLoading}
                    />
                  </div>
                  <p className="text-xs text-slate-400">Minimum 6 characters</p>
                </div>

                {/* P6.7: required privacy-policy consent */}
                <div className="flex items-start gap-2.5">
                  <Checkbox
                    id="privacy-consent"
                    checked={privacyConsent}
                    onCheckedChange={(v) => setPrivacyConsent(v === true)}
                    disabled={isLoading}
                    className="mt-0.5"
                    data-testid="signup-privacy-checkbox"
                  />
                  <Label
                    htmlFor="privacy-consent"
                    className="text-sm text-slate-300 font-normal leading-snug cursor-pointer"
                  >
                    I have read and accept the{' '}
                    <Link
                      to="/privacy"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-400 hover:text-blue-300 font-medium underline"
                      data-testid="signup-privacy-link"
                    >
                      Privacy Policy
                    </Link>
                  </Label>
                </div>

                <Button
                  type="submit"
                  className="w-full h-11 world-btn-primary font-medium btn-press"
                  disabled={isLoading || !privacyConsent}
                  data-testid="signup-submit-button"
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Creating account...
                    </>
                  ) : (
                    'Create account'
                  )}
                </Button>
              </form>
              )}

              <p className="mt-6 text-center text-sm text-slate-400">
                Already have an account?{' '}
                <Link
                  to="/login"
                  className="text-blue-400 hover:text-blue-300 font-medium"
                  data-testid="signup-login-link"
                >
                  Sign in
                </Link>
              </p>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Right Panel - Globe + tagline */}
      <div className="hidden lg:flex w-[58%] auth-bg-image relative overflow-hidden">
        <div className="absolute inset-0 auth-bg-overlay" />

        {/* Floating UI cards */}
        <div className="world-panel absolute left-[12%] top-[16%] p-3 flex items-center gap-2 opacity-80" aria-hidden="true">
          <BarChart3 className="h-5 w-5 text-blue-400" />
          <div className="h-1.5 w-16 rounded bg-blue-500/40" />
        </div>
        <div className="world-panel absolute right-[10%] top-[24%] p-3 opacity-80" aria-hidden="true">
          <Activity className="h-5 w-5 text-violet-400" />
        </div>
        <div className="world-panel absolute left-[14%] bottom-[16%] p-3 flex items-center gap-2 opacity-80" aria-hidden="true">
          <PieChart className="h-5 w-5 text-purple-400" />
          <div className="space-y-1">
            <div className="h-1.5 w-14 rounded bg-violet-500/40" />
            <div className="h-1.5 w-10 rounded bg-blue-500/30" />
          </div>
        </div>

        {/* Globe */}
        <div className="absolute right-[6%] top-1/2 -translate-y-1/2" aria-hidden="true">
          <WorldGlobe size={460} className="!relative" />
        </div>

        <div className="relative z-10 flex flex-col items-start justify-center w-full pl-16 pr-12">
          <div className="max-w-md">
            <h2 className="text-4xl font-bold text-white mb-4 leading-tight">
              Decision-support for sales teams
            </h2>
            <p className="text-slate-300 text-lg">
              Qualify B2C e-commerce leads smarter and faster with CiaoLead.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Signup;
